import { lazy, Suspense, Component, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { MapPin, Search, Crosshair, Grid2X2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { i18n } from "@/i18n";
import { AddressAutocomplete } from "@/components/ui/address-autocomplete";
import { DescriptionSummary } from "./DescriptionSummary";
import { StepFooter } from "./StepFooter";
import { GewerbegebietList } from "./GewerbegebietList";
import type { GewerbegebietZone } from "@/types/api";

const LocationMap = lazy(() =>
  import("./LocationMap").then((m) => ({ default: m.LocationMap })),
);

// MapErrorBoundary is a class component and cannot use hooks.
// It reads the error string from the i18n singleton directly.
class MapErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error) {
    console.error("Map render error:", error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-[340px] rounded-lg border border-border mb-4 bg-muted/20">
          <p className="text-sm text-muted-foreground">
            {i18n.t("wizard.locationStep.mapLoadError", { ns: "search" })}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

interface LocationStepProps {
  description: string;
  location: string;
  onLocationChange: (val: string) => void;
  searchRadius: number;
  onSearchRadiusChange: (val: number) => void;
  searchMode: "radius" | "zones";
  onSearchModeChange: (mode: "radius" | "zones") => void;
  selectedZones: GewerbegebietZone[];
  onSelectedZonesChange: (zones: GewerbegebietZone[]) => void;
  mapCenter: { lat: number; lng: number } | null;
  onMapCenterChange: (center: { lat: number; lng: number }) => void;
  onBack: () => void;
  onContinue: () => void;
  onSkipToReview: () => void;
}

export function LocationStep({
  description,
  location,
  onLocationChange,
  searchRadius,
  onSearchRadiusChange,
  searchMode,
  onSearchModeChange,
  selectedZones,
  onSelectedZonesChange,
  mapCenter,
  onMapCenterChange,
  onBack,
  onContinue,
  onSkipToReview,
}: LocationStepProps) {
  const { t } = useTranslation("search");
  const showMap =
    searchMode === "radius" && location.trim().length > 0 && mapCenter !== null;
  const continueDisabled = searchMode === "zones" && selectedZones.length === 0;

  function handleModeChange(mode: "radius" | "zones") {
    if (mode === "zones" && !mapCenter) {
      if (location.trim().length > 0) {
        toast.error(t("wizard.toasts.locationSelectionRequired"));
      } else {
        toast.error(t("wizard.toasts.locationRequired"));
      }
      return;
    }
    onSearchModeChange(mode);
  }

  return (
    <div className="space-y-0">
      <DescriptionSummary description={description} />

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            {t("wizard.locationStep.cardTitle")}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-3">
          {/* Location + Radius row */}
          <div className="grid grid-cols-[3fr_1fr] gap-3 items-start">
            {/* Location (75%) */}
            <div>
              <Label
                htmlFor="wizard-location"
                className="text-xs font-medium mb-1.5 flex items-center gap-1.5"
              >
                <Search className="h-3 w-3" />
                {t("wizard.locationStep.locationLabel")}
              </Label>
              <AddressAutocomplete
                value={location}
                onChange={onLocationChange}
                onCoordinatesChange={(lat, lng) =>
                  onMapCenterChange({ lat, lng })
                }
                types={["locality", "administrative_area_level_3"]}
                placeholder={t("wizard.locationStep.locationPlaceholder")}
                id="wizard-location"
              />
            </div>

            {/* Radius (25%) */}
            <div>
              <Label className="text-xs font-medium mb-1.5 block">
                {t("wizard.locationStep.radiusLabel")}
              </Label>
              <div className="flex items-center gap-1.5 mt-1.5">
                <Input
                  type="number"
                  value={searchRadius}
                  min={1}
                  max={500}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    if (!isNaN(n))
                      onSearchRadiusChange(Math.max(1, Math.min(500, n)));
                  }}
                  className="text-center"
                />
                <span className="text-[0.8125rem] text-muted-foreground whitespace-nowrap">
                  km
                </span>
              </div>
            </div>
          </div>

          {/* Section divider */}
          <div className="flex items-center gap-3 pt-1">
            <hr className="flex-1 border-border" />
            <span className="text-[0.75rem] font-medium text-muted-foreground whitespace-nowrap">
              {t("wizard.locationStep.searchModeLabel")}
            </span>
            <hr className="flex-1 border-border" />
          </div>

          {/* Mode switcher */}
          <div className="flex border border-border rounded-[var(--radius)] overflow-hidden bg-muted">
            {/* Umkreissuche option */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => handleModeChange("radius")}
              onKeyDown={(e) => e.key === "Enter" && handleModeChange("radius")}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-[0.8125rem] font-medium cursor-pointer transition-all border-2 border-transparent select-none",
                searchMode === "radius"
                  ? "bg-card text-primary font-semibold !border-primary rounded-[calc(var(--radius)-1px)]"
                  : "text-muted-foreground border-r border-border",
              )}
            >
              {/* Radio dot */}
              <div
                className={cn(
                  "flex items-center justify-center w-4 h-4 rounded-full border-2 shrink-0",
                  searchMode === "radius" ? "border-primary" : "border-border",
                )}
              >
                {searchMode === "radius" && (
                  <div className="w-2 h-2 rounded-full bg-primary" />
                )}
              </div>
              <Crosshair className="h-3.5 w-3.5 shrink-0" />
              {t("wizard.locationStep.radiusMode")}
            </div>

            {/* Gewerbegebiete option */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => handleModeChange("zones")}
              onKeyDown={(e) => e.key === "Enter" && handleModeChange("zones")}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-[0.8125rem] font-medium cursor-pointer transition-all border-2 border-transparent select-none",
                searchMode === "zones"
                  ? "bg-card text-primary font-semibold !border-primary rounded-[calc(var(--radius)-1px)]"
                  : "text-muted-foreground",
              )}
            >
              {/* Radio dot */}
              <div
                className={cn(
                  "flex items-center justify-center w-4 h-4 rounded-full border-2 shrink-0",
                  searchMode === "zones" ? "border-primary" : "border-border",
                )}
              >
                {searchMode === "zones" && (
                  <div className="w-2 h-2 rounded-full bg-primary" />
                )}
              </div>
              <Grid2X2 className="h-3.5 w-3.5 shrink-0" />
              {t("wizard.locationStep.zonesMode")}
            </div>
          </div>

          {/* Mode panels */}
          {searchMode === "radius" && showMap && (
            <MapErrorBoundary>
              <Suspense
                fallback={
                  <div className="flex items-center justify-center h-[340px] rounded-lg border border-border mb-4 bg-muted/20">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                }
              >
                <LocationMap center={mapCenter!} radiusKm={searchRadius} />
              </Suspense>
            </MapErrorBoundary>
          )}

          {searchMode === "zones" && mapCenter && (
            <GewerbegebietList
              lat={mapCenter.lat}
              lng={mapCenter.lng}
              radiusKm={searchRadius}
              selectedZones={selectedZones}
              onSelectedZonesChange={onSelectedZonesChange}
            />
          )}
        </CardContent>
      </Card>

      <StepFooter
        onBack={onBack}
        onContinue={onContinue}
        onSkipToReview={onSkipToReview}
        continueDisabled={continueDisabled}
      />
    </div>
  );
}
